import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Index, Configure } from 'react-instantsearch';
import { Row, Column, List } from '@pella/sash-framework';
import { CTA } from '../../../';

import SearchField from './search-box';
import IndexResults from './index-results';

import styles from './index.module.scss';

const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY);

const SiteSearch = ({ mobileSearch }) => {
	const configData = useStaticQuery(gqlQuery).contentstackSearchConfig;
	const [query, setQuery] = useState('');
	const [focus, setFocus] = useState(false);
	const searchRef = useRef(null);
	const appendEnv = process.env.GATSBY_CONTENTSTACK_ENVIRONMENT !== 'production' ? '-dev' : '';

	const indices = [
		{ name: `Shop${appendEnv}`, title: `Shop`, hitComp: `ProductHitLink` },
		{ name: `Ideas${appendEnv}`, title: `Ideas`, hitComp: `HitLink` },
	];

	useEffect(() => {
		document.addEventListener('mousedown', onOutsideClick);
		document.addEventListener('touchstart', onOutsideClick);

		return () => {
			document.removeEventListener('mousedown', onOutsideClick);
			document.removeEventListener('touchstart', onOutsideClick);
		};
	}, []);

	useEffect(() => {
		if (mobileSearch) {
			setTimeout(() => document.getElementById('siteSearch')?.focus(), 100);
			setFocus(true);
		}
	}, [mobileSearch]);

	const onOutsideClick = (e) => {
		if (e.target && searchRef?.current && !searchRef.current.contains(e.target)) {
			setFocus(false);
		}
	};

	const setSearchFocus = () => setFocus(true);

	const onSearchFieldChange = (value) => setQuery(value);

	return (
		<InstantSearch
			indexName={indices[0].name}
			searchClient={searchClient}
			future={{ preserveSharedStateOnUnmount: true }}
			insights
		>
			<div
				ref={searchRef}
				className={mobileSearch ? styles.siteSearchWrapper_active : styles.siteSearchWrapper}
			>
				<div className={`${styles.siteSearchMain} ${focus ? styles.siteSearch_tail : ''}`}>
					<SearchField
						onFocus={setSearchFocus}
						placeholder='Search products, advice'
						onInputChange={onSearchFieldChange}
					/>
				</div>

				{focus && (
					<div className={`${styles.siteSearch_HitsWrapper} ${!query ? styles.hitsWrapper_EmptyState : ''}`}>
						<Configure
							hitsPerPage={4}
							snippetEllipsisText='...'
							clickAnalytics
						/>

						{!query && (
							<div className={styles.siteSearch_RecommendedWrapper}>
								{configData?.recommended_title && <p className='text-sm uppercase tracking-wider text-neutral-600'>{configData.recommended_title}</p>}
								<List
									bullets='none'
									layout='inline'
								>
									{configData?.recommended?.recommended_item?.[0] &&
										configData?.recommended?.recommended_item.map((item, index) => (
											<li key={index}>
												<a
													href={`/search/?query=${item}`}
													className={styles.hitsWrapper_RecommendedItem}
												>
													{item}
												</a>
											</li>
										))}
								</List>
							</div>
						)}

						{query && query.length > 0 && (
							<Row className={styles.siteSearch_SearchResultsRow}>
								{indices.map(({ name, title, hitComp }) => (
									<Column
										key={name}
										xs={12}
										md={6}
										lg='fluid'
									>
										<Index indexName={name}>
											<IndexResults
												indexTitle={title}
												hitComponent={hitComp}
												resultsClassName='grid grid-cols-1 gap-x-1 sm:grid-cols-2 md:gap-x-4 lg:grid-cols-1 xl:grid-cols-2 mb-5 pt-5 xl:pt-2.5'
											/>
										</Index>
									</Column>
								))}

								<Column
									xs={12}
									lg={3}
								>
									{configData?.support_title && <p className='text-sm uppercase tracking-wider text-neutral-600'>{configData.support_title}</p>}
									{configData?.support_group?.ctas?.[0] && (
										<List
											bullets='none'
											className='divide-grays-15 divide-y'
										>
											{configData.support_group.ctas.map((cta, index) => (
												<li
													key={index}
													className='py-2.5 font-semibold'
												>
													<CTA
														data={cta}
														className='block no-underline hover:text-neutral-600'
													/>
												</li>
											))}
										</List>
									)}
								</Column>
							</Row>
						)}
					</div>
				)}
			</div>
		</InstantSearch>
	);
};

SiteSearch.propTypes = {
	indices: PropTypes.array,
	mobileSearch: PropTypes.bool,
};

export default SiteSearch;

export const gqlQuery = graphql`
	{
		contentstackSearchConfig {
			support_title
			recommended_title
			recommended {
				recommended_item
			}
			support_group {
				ctas {
					...CTA
				}
			}
			no_results
		}
	}
`;
